/**
 * Hook personalizado para obtener el contador de visitas del usuario.
 * Lee directamente de la tabla interaction_counter con TanStack Query para caché automático.
 * 
 * @param {Object} user - Usuario autenticado
 * @returns {Object} - Resultado de la query con contador de visitas
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export const useVisitCount = (user) => {
  return useQuery({
    /**
     * Query key única por usuario
     */
    queryKey: ['visitCount', user?.id],
    
    /**
     * Solo ejecutar si hay usuario
     */
    enabled: !!user?.id,
    
    /**
     * Función para obtener contador de visitas
     */
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('Usuario no disponible');
      }

      try {
        console.log('[useVisitCount] Obteniendo contador de visitas para:', user.id);

        const { data, error } = await supabase
          .from('interaction_counter')
          .select('visitas')
          .eq('user_id', user.id)
          .single();

        if (error) {
          // Si no hay registro, retornar 0
          if (error.code === 'PGRST116') {
            console.log('[useVisitCount] No hay registro de contador, retornando 0');
            return { visitas: 0 };
          }
          throw error;
        }

        console.log('[useVisitCount] Contador obtenido:', data?.visitas || 0);
        return data?.visitas || 0;
      } catch (err) {
        console.error('[useVisitCount] Error obteniendo contador:', err);
        throw err;
      }
    },
    
    /**
     * Tiempo de caché: 2 segundos
     * Más rápido para responder a cambios en tiempo real
     */
    staleTime: 2 * 1000,
    
    /**
     * No refrescar automáticamente
     * Dependeremos de Supabase Realtime para actualizaciones
     */
    refetchInterval: false,
    
    /**
     * Estrategia de reintentos
     */
    retry: (failureCount, error) => {
      // No reintentar para errores de JWT expired
      if (error?.code === 'PGRST301') {
        return false;
      }
      return failureCount < 3;
    },
    
    /**
     * Callback cuando hay error en la query
     */
    onError: (error) => {
      if (error?.code !== 'PGRST301') {
        console.error('[useVisitCount] Error en query:', error);
      }
    },
  });
};
